import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** Active scroll-lock depth across all dialogs (e.g. quick-add → cart-drawer handoff). */
let scrollLockCount = 0;

/** Scroll position preserved while any dialog holds the lock. */
let lockedScrollY = 0;

/**
 * @returns {number}
 */
function getLockedScrollY() {
  if (document.body.style.position === 'fixed') {
    const top = parseInt(document.body.style.top, 10);
    if (!Number.isNaN(top)) return Math.abs(top);
  }
  return window.scrollY;
}

/**
 * @param {number} scrollY
 */
function applyScrollLock(scrollY) {
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
}

/**
 * Releases scroll lock and restores the saved scroll position.
 */
function releaseScrollLock() {
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Guards against re-entrant show/close calls. `showModal()` is deferred to a
   * `requestAnimationFrame` and `close()` is deferred behind an animation `await`,
   * so `dialog.open` lags behind the moment we've already committed to opening or
   * closing — a second call arriving inside that window (e.g. two listeners
   * reacting to the same cart-update event during a quick-add → cart-drawer
   * handoff) would double up the `scrollLockCount` increment/decrement and throw
   * the shared lock accounting out of sync, leaving the page stuck or restoring
   * scroll to the wrong position.
   */
  #isOpening = false;
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#isOpening) return;
    this.#isOpening = true;

    if (scrollLockCount === 0) {
      lockedScrollY = getLockedScrollY();
    }
    scrollLockCount++;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      applyScrollLock(lockedScrollY);

      dialog.showModal();
      this.#isOpening = false;
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;
    this.#isClosing = true;

    try {
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      // Close first, while the page is still scroll-locked (body pinned via
      // position: fixed). Native <dialog> close() restores focus to whatever
      // triggered showModal(), and the browser scrolls that element into view —
      // while locked, window.scrollY is stuck at 0 with nothing scrollable, so
      // that native scroll is a no-op. Releasing the lock afterwards, as the very
      // last step, means nothing runs after it to fight with our restored scroll
      // position (previously this caused a visible flash to the top before
      // snapping back down).
      dialog.close();
      dialog.classList.remove('dialog-closing');

      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        releaseScrollLock();
      }

      this.dispatchEvent(new DialogCloseEvent());
    } finally {
      this.#isClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
